import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { HStack, Text } from '@fhs-legacy/universal-components';
import { ISanityImage } from '@rbi-ctg/menu';
import { Calories } from 'components/price-and-calories';
import useEffectOnce from 'hooks/use-effect-once';
import { useFormatCalories } from 'hooks/use-format-calories';
import { CustomEventNames, EventTypes, useCRMEventsContext } from 'state/crm-events';
import { useUIContext } from 'state/ui';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';

import {
  UpsellItemButton,
  UpsellItemCalories,
  UpsellItemDetails,
  UpsellItemIcon,
  UpsellItemName,
  UpsellItemPicture,
  UpsellItemPrice,
} from './styles';
import { IUpsellItemProps } from './types';

const UpsellItem: React.FC<React.PropsWithChildren<IUpsellItemProps>> = ({ item, addToCard }) => {
  const { logRBIEvent } = useCRMEventsContext();
  const { formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const { formatCurrencyForLocale } = useUIContext();
  const itemCalories = item.nutrition?.calories ?? null;

  const formattedPrice = useMemo(
    () => formatCurrencyForLocale(item.price ?? 0),
    [item, formatCurrencyForLocale]
  );

  const calories = useMemo(() => formatCalories(itemCalories), [formatCalories, itemCalories]);

  useEffectOnce(() => {
    logRBIEvent({
      name: CustomEventNames.UPSELL_DISPLAYED,
      type: EventTypes.Other,
      attributes: {
        name: item.name,
        sanityId: item.sanityId,
        price: (item.price ?? 0) / 100,
      },
    });
  });

  return (
    <UpsellItemButton
      testID="upsell-add-to-order-btn"
      accessibilityLabel={formatMessage({ id: 'addItemLabel' }, { itemName: item.name })}
      onPress={() => {
        hapticImpact({ style: HapticsImpactStyle.Heavy });

        addToCard(item);
      }}
    >
      <UpsellItemPicture image={item.image as unknown as ISanityImage} alt={item.name!} />
      <UpsellItemDetails
        accessibilityLabel={formatMessage(
          { id: 'upsellItemLabel' },
          {
            itemName: item.name,
            calories: formatCalories(itemCalories),
            price: formattedPrice,
          }
        )}
      >
        <UpsellItemName testID={`upsell-name-${item.sanityId}`}>{item.name}</UpsellItemName>
        <HStack>
          <UpsellItemPrice testID={`upsell-price-${item.sanityId}`}>
            <Text testID={`upsell-price-text-${item.sanityId}`}>{formattedPrice}</Text>
          </UpsellItemPrice>
          {calories && (
            <Calories>
              <Text variant="copyOne"> | </Text>
              <UpsellItemCalories testID={`upsell-calories-${item.sanityId}`}>
                {calories}
              </UpsellItemCalories>
            </Calories>
          )}
        </HStack>
      </UpsellItemDetails>
      <UpsellItemIcon variant="add" flex={0} />
    </UpsellItemButton>
  );
};

export default UpsellItem;
